import { ExportRow } from '../../../../../hooks/exportDataHooks'
import { AdDataAggregate, AdDataAggregates, AdDataSearchForm, AdDataSearchRequest, AttributeScores } from '../../types/AdData'

export const artStyleOptions = ['Cartoonish', 'Manga/Anime', 'Pixel-retro', 'Realistic']
export const artGenreOptions = ['Fantasy/Historic', 'Modern', 'Scifi/Future']
export const adDataGameFields = [
  'appId',
  'name',
  'artist',
  'images',
  'conventionalSubgenre',
  'conventionalSubgenreId',
  'srank',
  'sdrank',
  'appGenreSranks',
  'appGenreSdranks',
  'market',
]
export const emptyAdDataSearchForm: AdDataSearchForm = { appId: '', conventionalSubgenreId: '', artStyleId: '', artGenreId: '', appStoreGenreId: '' }

type NamedItem = { id: string; name: string }
type GenreRanks = { [appStoreGenreId: string]: number }

export type AdDataGame = {
  appId: number
  name: string
  artist: string
  images?: string[]
  market: string
  conventionalSubgenre?: string
  srank?: number
  sdrank?: number
  appGenreSranks?: GenreRanks
  appGenreSdranks?: GenreRanks
}

export type AdDataRow = {
  appId: number
  name: string
  artist: string
  icon?: string
  market: string
  conventionalSubgenre?: string
  modelSubgenre?: string
  artStyle?: string
  artGenre?: string
  saturation?: number
  lightness?: number
  srank?: number
  sdrank?: number
  appGenreSranks: GenreRanks
  appGenreSdranks: GenreRanks
  aggregate: AdDataAggregate
}

export const parseAppId = (value: string) => {
  const trimmed = value.trim()
  return /^\d+$/.test(trimmed) && Number(trimmed) > 0 ? Number(trimmed) : undefined
}

export const isAppIdInvalid = (value: string) => value.trim() !== '' && parseAppId(value) === undefined

export const hasAdDataFilters = (form: AdDataSearchForm) => !!(form.conventionalSubgenreId || form.artStyleId || form.artGenreId || form.appStoreGenreId)

export const isAdDataSearchReady = (form: AdDataSearchForm) => (form.appId.trim() ? parseAppId(form.appId) !== undefined : hasAdDataFilters(form))

// The backend ignores the filters when an app id is given, so they are sent empty.
export const buildAdDataSearchRequest = (form: AdDataSearchForm): AdDataSearchRequest => {
  const appId = parseAppId(form.appId)
  if (appId !== undefined) return { appIds: [appId], conventionalSubgenreId: null, artStyleId: null, artGenreId: null, appStoreGenreId: null }
  return {
    appIds: [],
    conventionalSubgenreId: form.conventionalSubgenreId || null,
    artStyleId: form.artStyleId || null,
    artGenreId: form.artGenreId || null,
    appStoreGenreId: form.appStoreGenreId ? Number(form.appStoreGenreId) : null,
  }
}

export const getAnalysedAppIds = (aggregates: AdDataAggregates | undefined) =>
  Object.entries(aggregates ?? {})
    .filter(([, aggregate]) => aggregate && Object.keys(aggregate).length > 0)
    .map(([appId]) => Number(appId))

const findName = (items: NamedItem[], id: string | undefined) => (id ? items.find((item) => item.id === id)?.name : undefined)

const rankOrUndefined = (rank: number | undefined) => (rank ? rank : undefined)

// Only games that rank in the market are listed, as in legacy.
export const buildAdDataRows = (games: AdDataGame[], aggregates: AdDataAggregates, subgenres: NamedItem[]): AdDataRow[] =>
  games
    .filter((game) => aggregates[String(game.appId)])
    .map((game) => {
      const aggregate = aggregates[String(game.appId)]
      return {
        appId: game.appId,
        name: game.name,
        artist: game.artist,
        icon: game.images?.[1] ?? game.images?.[0],
        market: game.market,
        conventionalSubgenre: game.conventionalSubgenre || undefined,
        modelSubgenre: findName(subgenres, aggregate.subgenre?.highestAttribute),
        artStyle: aggregate.style?.highestAttribute,
        artGenre: aggregate.genre?.highestAttribute,
        saturation: aggregate.color?.saturationAverage,
        lightness: aggregate.color?.lightnessAverage,
        srank: rankOrUndefined(game.srank),
        sdrank: rankOrUndefined(game.sdrank),
        appGenreSranks: game.appGenreSranks ?? {},
        appGenreSdranks: game.appGenreSdranks ?? {},
        aggregate,
      }
    })

export const getSubgenreMatch = (row: Pick<AdDataRow, 'modelSubgenre' | 'conventionalSubgenre'>) =>
  row.modelSubgenre && row.conventionalSubgenre ? (row.modelSubgenre === row.conventionalSubgenre ? 'match' : 'mismatch') : undefined

export const toPercent = (value: number | undefined | null) =>
  value === undefined || value === null || Number.isNaN(value) ? undefined : Math.round(value * 100)

export const getAppStoreGenreName = (genreMap: { [id: string]: string }, id: string) => genreMap[id] ?? id

const collectKeys = (rows: AdDataRow[], keysOf: (row: AdDataRow) => string[]) => {
  const keys = new Set<string>()
  rows.forEach((row) => keysOf(row).forEach((key) => keys.add(key)))
  return Array.from(keys)
}

const scoreOrEmpty = (scores: AttributeScores | undefined, key: string) => scores?.[key] ?? ''

// Legacy's columns: every row gets the union of the per-game columns, empty when the game has no value.
export const buildAdDataExportRows = (rows: AdDataRow[], subgenres: NamedItem[], genreMap: { [id: string]: string }): ExportRow[] => {
  const demographics = collectKeys(rows, (row) => Object.keys(row.aggregate.demographics?.attributes ?? {}))
  const colors = collectKeys(rows, (row) => Object.keys(row.aggregate.color?.colorGroups ?? {}))
  const sgrGenres = collectKeys(rows, (row) => Object.keys(row.appGenreSranks))
  const sdrGenres = collectKeys(rows, (row) => Object.keys(row.appGenreSdranks))
  const subgenreIds = collectKeys(rows, (row) => Object.keys(row.aggregate.subgenre?.attributes ?? {})).filter((id) => findName(subgenres, id))
  const features = collectKeys(rows, (row) => Object.keys(row.aggregate.feature?.attributes ?? {}))
  const styles = collectKeys(rows, (row) => Object.keys(row.aggregate.style?.attributes ?? {}))
  const genres = collectKeys(rows, (row) => Object.keys(row.aggregate.genre?.attributes ?? {}))

  return rows.map((row) => {
    const { aggregate } = row
    const exportRow: ExportRow = {
      'App ID': row.appId,
      Name: row.name,
      Publisher: row.artist,
      'Model Subgenre': row.modelSubgenre ?? '',
      'Conventional Subgenre': row.conventionalSubgenre ?? '',
      'Art Style': row.artStyle ?? '',
      'Art Genre': row.artGenre ?? '',
    }
    demographics.forEach((key) => (exportRow[key.replace('demographics', '')] = scoreOrEmpty(aggregate.demographics?.attributes, key)))
    exportRow['Saturation AVG'] = row.saturation ?? ''
    exportRow['Lightness AVG'] = row.lightness ?? ''
    colors.forEach((key) => (exportRow[`Color - ${key}`] = scoreOrEmpty(aggregate.color?.colorGroups, key)))
    exportRow['SGR - Games'] = row.srank ?? ''
    sgrGenres.forEach((id) => (exportRow[`SGR - ${getAppStoreGenreName(genreMap, id)}`] = row.appGenreSranks[id] ?? ''))
    exportRow['SDR - Games'] = row.sdrank ?? ''
    sdrGenres.forEach((id) => (exportRow[`SDR - ${getAppStoreGenreName(genreMap, id)}`] = row.appGenreSdranks[id] ?? ''))
    subgenreIds.forEach((id) => (exportRow[`Subgenre - ${findName(subgenres, id)}`] = scoreOrEmpty(aggregate.subgenre?.attributes, id)))
    features.forEach((key) => (exportRow[`Feature - ${key}`] = scoreOrEmpty(aggregate.feature?.attributes, key)))
    styles.forEach((key) => (exportRow[`Art Style - ${key}`] = scoreOrEmpty(aggregate.style?.attributes, key)))
    genres.forEach((key) => (exportRow[`Art Genre - ${key}`] = scoreOrEmpty(aggregate.genre?.attributes, key)))
    return exportRow
  })
}

export type AttributeGroupKey =
  | 'style'
  | 'mood'
  | 'genre'
  | 'subgenre'
  | 'feature'
  | 'demographics'
  | 'color'
  | 'icon_mood'
  | 'icon_style'
  | 'motivation_final_attributes'
  | 'motivation_final_segments'
  | 'score'

// `value` is the bar's share (0–1); `actualValue` is shown when the stored value is on another scale.
export type VisualAttribute = { name: string; value: number; actualValue?: number }
export type AttributeGroup = { key: AttributeGroupKey; isColor: boolean; attributes: VisualAttribute[] }

const toAttributes = (scores: AttributeScores | undefined, scale = 1, nameOf: (key: string) => string = (key) => key): VisualAttribute[] =>
  Object.entries(scores ?? {}).map(([key, value]) => ({ name: nameOf(key), value: value / scale, ...(scale !== 1 ? { actualValue: value } : {}) }))

// Legacy's order and scales: motivation attributes are 0–5 and scores 0–10.
export const buildAttributeGroups = (aggregate: AdDataAggregate, subgenres: NamedItem[]): AttributeGroup[] => {
  const groups: [AttributeGroupKey, VisualAttribute[]][] = [
    ['style', toAttributes(aggregate.style?.attributes)],
    ['mood', toAttributes(aggregate.mood?.attributes)],
    ['genre', toAttributes(aggregate.genre?.attributes)],
    ['subgenre', toAttributes(aggregate.subgenre?.masterAttributes, 1, (id) => findName(subgenres, id) ?? id)],
    ['feature', toAttributes(aggregate.feature?.attributes)],
    ['demographics', toAttributes(aggregate.demographics?.attributes)],
    ['color', toAttributes(aggregate.color?.colorGroups)],
    ['icon_mood', toAttributes(aggregate.icon_mood?.attributes)],
    ['icon_style', toAttributes(aggregate.icon_style?.attributes)],
    ['motivation_final_attributes', toAttributes(aggregate.motivation?.finalAttributes, 5)],
    ['motivation_final_segments', toAttributes(aggregate.motivation?.finalSegments)],
    ['score', toAttributes(aggregate.score, 10)],
  ]
  return groups
    .filter(([, attributes]) => attributes.length > 0)
    .map(([key, attributes]) => ({ key, isColor: key === 'color', attributes: [...attributes].sort((a, b) => b.value - a.value) }))
}
